import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { can } from "@/lib/rbac";

const FACILITY = "H. K. INDUSTRIES"; // single-plant deployment: H. K. INDUSTRIES (D-54)

// Owns all dashboard server state, data loading, realtime sync and drag presence.
export function useDashboardData(role, userName, userId) {
  const [orders, setOrders] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [downtimes, setDowntimes] = useState([]);
  const [bomItems, setBomItems] = useState([]);
  const [shortages, setShortages] = useState({});
  const [weekShortages, setWeekShortages] = useState({ materials: [], orders_checked: 0, horizon_days: 7 });
  const [purchaseSuggestions, setPurchaseSuggestions] = useState([]);
  const [poSummary, setPoSummary] = useState({ overdue_count: 0, pending_approval_count: 0, open_item_ids: [] });
  const [poVersion, setPoVersion] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [incoming, setIncoming] = useState({});
  const [ospVendors, setOspVendors] = useState([]);
  const [gatePasses, setGatePasses] = useState([]);
  const [activeDrags, setActiveDrags] = useState({}); // orderId -> { user, client_id }
  const [wsReady, setWsReady] = useState(false); // defer WS connect until first authed load succeeds
  const [lockoutTick, setLockoutTick] = useState(0); // bumped on security.lockout to refresh admin alerts
  const [deviceTick, setDeviceTick] = useState(0); // bumped on device-gate events to refresh admin device lists/badges
  const clientId = useRef(Math.random().toString(36).slice(2));
  const myDragging = useRef(null);

  const loadCore = useCallback(async () => {
    try {
      const [o, m, k, d, b, sh, ps, wk] = await Promise.all([
        api.get(`/orders?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/machines?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/dashboard/kpis?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/downtime?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/bom/items?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/orders/shortages?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/purchase-suggestions?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/orders/shortages/week?facility=${encodeURIComponent(FACILITY)}`),
      ]);
      const [po, sup, inc] = await Promise.all([
        api.get(`/purchase-orders/summary?facility=${encodeURIComponent(FACILITY)}`),
        api.get(`/suppliers`),
        api.get(`/purchase-orders/incoming?facility=${encodeURIComponent(FACILITY)}`),
      ]);
      setOrders(o);
      setWorkOrders(await api.get(`/orders?facility=${encodeURIComponent(FACILITY)}&kind=work_order`));
      setMachines(m);
      setKpis(k);
      setDowntimes(d);
      setBomItems(b);
      setShortages(sh.shortages || {});
      setWeekShortages(wk);
      setPurchaseSuggestions(ps.suggestions || []);
      setPoSummary(po);
      setPoVersion((v) => v + 1);
      setSuppliers(sup);
      setCustomers(await api.get(`/customers`));
      setSalesOrders(await api.get(`/sales-orders?facility=${encodeURIComponent(FACILITY)}`));
      setIncoming(inc.incoming || {});
      const [ospV, gp] = await Promise.all([
        api.get(`/osp/vendors`),
        api.get(`/osp/gate-passes?facility=${encodeURIComponent(FACILITY)}`),
      ]);
      setOspVendors(ospV);
      setGatePasses(gp);
      setWsReady(true); // cookie proven valid — safe to open the realtime socket
    } catch (e) {
      logger.error("Failed to load dashboard data:", e);
    }
  }, [setOrders, setMachines, setKpis, setDowntimes, setBomItems, setShortages, setPurchaseSuggestions, setPoSummary, setPoVersion,
    setSuppliers, setCustomers, setSalesOrders, setIncoming, setOspVendors, setGatePasses, setWsReady, setWorkOrders]);

  const reloadFacilities = useCallback(() => {
    api.get("/facilities").then(setFacilities).catch((e) => logger.error("Failed to load facilities:", e));
  }, [setFacilities]);

  const loadUsers = useCallback(async () => {
    if (!can.viewUsers(role)) return;
    try {
      setUsers(await api.get("/users"));
    } catch (e) {
      logger.error("Failed to load users:", e);
    }
  }, [role, setUsers]);

  useEffect(() => {
    reloadFacilities();
  }, [reloadFacilities]);

  useEffect(() => {
    loadCore();
    loadUsers();
  }, [loadCore, loadUsers]);

  const realtime = useRealtime(
    useCallback(
      (msg) => {
        if (!msg || !msg.type) return;
        if (msg.type === "session.kick") {
          // Another device logged into this account — re-validate; a stale token 401s and
          // api.js dispatches proplan:session-kick, logging this (old) device out with a message.
          if (!msg.payload?.user_id || msg.payload.user_id === userId) api.get("/auth/me").catch(() => {});
          return;
        }
        if (msg.type.startsWith("presence.")) {
          const { order_id, user: who, client_id } = msg.payload || {};
          if (client_id === clientId.current) return; // ignore own echoes
          if (msg.type === "presence.drag_start") {
            setActiveDrags((prev) => ({ ...prev, [order_id]: { user: who, client_id } }));
            if (myDragging.current === order_id) {
              toast.warning(`${who} is moving the same order right now — coordinate before dropping!`, { duration: 4000 });
            }
          } else {
            setActiveDrags((prev) => {
              const next = { ...prev };
              delete next[order_id];
              return next;
            });
          }
          return;
        }
        if (msg.type === "order.updated" && myDragging.current && msg.payload?.id === myDragging.current) {
          toast.warning("This order was just rescheduled by another user — schedule refreshed", { duration: 4000 });
        }
        if (msg.type === "po.created" && msg.payload?.needs_approval && role === "admin") {
          toast.warning(`New PO ${msg.payload.po_number} from ${msg.payload.supplier || "a planner"} is awaiting your approval`, { duration: 5000 });
        }
        if (msg.type === "approval.requested" && role === "admin") {
          toast.warning(`New ${msg.payload?.kind || "record"} "${msg.payload?.name}" from ${msg.payload?.by || "a planner"} is awaiting your approval`, { duration: 5000 });
        }
        if (msg.type === "order.bom_refreshed" && role === "operator") {
          toast.warning(`Production stages updated for ${msg.payload?.order_code} to the latest BOM. Please review the new steps before logging your next quantity.`, { duration: 8000 });
        }
        if (msg.type === "security.lockout" && role === "admin") {
          toast.error(`🔒 Account "${msg.payload?.username}" locked after ${msg.payload?.attempts} failed logins`, { duration: 7000 });
          setLockoutTick((t) => t + 1);
        }
        if (msg.type === "security.untrusted_device" && role === "admin") {
          toast.error(`New device blocked for "${msg.payload?.username}" (${msg.payload?.device_label || "unknown device"}) — approve it in Trusted Devices`, {
            duration: 10000,
            action: { label: "Review", onClick: () => window.dispatchEvent(new CustomEvent("proplan:navigate", { detail: "users" })) },
          });
          setDeviceTick((t) => t + 1);
        }
        if (msg.type === "security.device_approved" && role === "admin") {
          toast.success(`Device approved for "${msg.payload?.username}" by ${msg.payload?.by || "admin"}`, { duration: 4000 });
          setDeviceTick((t) => t + 1);
        }
        if (msg.type === "security.device_renamed" && role === "admin") {
          toast.info(`Device for "${msg.payload?.username}" renamed to "${msg.payload?.label}"`, { duration: 4000 });
          setDeviceTick((t) => t + 1);
        }
        if (msg.type.startsWith("order.") || msg.type.startsWith("machine.") || msg.type.startsWith("downtime.") || msg.type.startsWith("bom.") || msg.type.startsWith("po.") || msg.type.startsWith("sales.") || msg.type.startsWith("osp.") || msg.type.startsWith("supplier.") || msg.type.startsWith("customer.") || msg.type.startsWith("approval.")) loadCore();
        if (msg.type.startsWith("facility.")) {
          reloadFacilities();
          loadCore();
        }
        if (msg.type.startsWith("user.")) loadUsers();
        if (msg.type !== "connected" && !msg.type.startsWith("security.") && !msg.type.startsWith("session.") && !msg.type.startsWith("approval.") && msg.type !== "order.bom_refreshed") {
          const verb = msg.type.split(".")[1];
          toast.info(`Live update: ${msg.type.split(".")[0]} ${verb}`, { duration: 1800 });
        }
      },
      [loadCore, loadUsers, reloadFacilities, role, userId, clientId, myDragging, setActiveDrags, setLockoutTick, setDeviceTick]
    ),
    wsReady
  );

  const handleDragPresence = useCallback(
    (orderId, isStart) => {
      myDragging.current = isStart ? orderId : null;
      realtime.send({
        type: isStart ? "presence.drag_start" : "presence.drag_end",
        order_id: orderId,
        user: userName,
        client_id: clientId.current,
      });
    },
    [realtime, userName, clientId, myDragging]
  );

  return {
    facility: FACILITY,
    orders,
    setOrders,
    workOrders,
    machines,
    users,
    kpis,
    facilities,
    downtimes,
    bomItems,
    shortages,
    weekShortages,
    purchaseSuggestions,
    poSummary,
    poVersion,
    suppliers,
    customers,
    salesOrders,
    incoming,
    ospVendors,
    gatePasses,
    activeDrags,
    loadCore,
    loadUsers,
    reloadFacilities,
    realtime,
    handleDragPresence,
    lockoutTick,
    deviceTick,
  };
}
